import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  useMsal, 
  useIsAuthenticated,
  useAccount
} from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { silentRequest, loginRequest } from '../config/authConfig';
import { apiService } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  isLoading: boolean;
  groups: string[];
  hasAnyGroup: (requiredGroups?: string[]) => boolean;
  updateGroups: (mutator: (previous: string[]) => string[]) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

const extractGroups = (accountInfo: AccountInfo | null | undefined): string[] => {
  if (!accountInfo) {
    return [];
  }

  const claims = accountInfo.idTokenClaims as Record<string, unknown> | undefined;
  if (!claims) {
    return [];
  }

  const groupClaim = claims["groups"];
  const roleClaim = claims["roles"];
  const appRolesClaim = claims["appRoles"];

  // Also check for _claim_names and _claim_sources (used when groups exceed limit)
  const claimNames = claims["_claim_names"] as Record<string, string> | undefined;

  if (process.env.NODE_ENV === 'development' && claimNames && claimNames.groups) {
    console.log('[AuthContext] Groups claim uses Microsoft Graph endpoint (too many groups for token)');
  }

  const normalizeClaim = (claimValue: unknown): string[] => {
    if (!claimValue) {
      return [];
    }
    if (Array.isArray(claimValue)) {
      return claimValue.filter((value): value is string => typeof value === 'string');
    }
    if (typeof claimValue === 'string') {
      return [claimValue];
    }
    return [];
  };

  const combined = [
    ...normalizeClaim(groupClaim),
    ...normalizeClaim(roleClaim),
    ...normalizeClaim(appRolesClaim)
  ];

  // Ensure uniqueness and preserve stable order
  const result = Array.from(new Set(combined));
  return result;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || null);
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState<string[]>(() => {
    // Initialize groups from account if available immediately
    return extractGroups(accounts[0] || null);
  });

  const acquireApiToken = useCallback(async (activeAccount: AccountInfo): Promise<string | null> => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...silentRequest,
        account: activeAccount
      });

      return tokenResponse.accessToken;
    } catch (error: any) {
      if (error?.errorCode === 'interaction_in_progress') {
        return null;
      }

      try {
        const tokenResponse = await instance.acquireTokenPopup({
          ...silentRequest,
          account: activeAccount
        });

        return tokenResponse.accessToken;
      } catch (popupError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[AuthContext] API token popup acquisition failed', popupError);
        }
        return null;
      }
    }
  }, [instance]);

  // Set up token provider for API service
  useEffect(() => {
    const tokenProvider = async (): Promise<string | null> => {
      if (!isAuthenticated || !instance.getActiveAccount()) {
        return null;
      }

      try {
        const activeAccount = instance.getActiveAccount();
        if (!activeAccount) {
          return null;
        }

        return await acquireApiToken(activeAccount);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[AuthContext] Token acquisition failed in provider');
        }
        return null;
      }
    };

    apiService.setTokenProvider(tokenProvider);
  }, [acquireApiToken, instance, isAuthenticated]);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Wait for MSAL to be ready (it's initialized in msalInstance.ts)
        // The initialize() call is idempotent, so it's safe to call multiple times
        await instance.initialize();
        
        // Only handle redirect promise once on initial mount
        if (isMounted) {
          const redirectResult = await instance.handleRedirectPromise();
          
          // Only process if we actually have a redirect result
          if (redirectResult && isMounted) {
            apiService.setAuthToken(redirectResult.accessToken);
            // Set the active account
            instance.setActiveAccount(redirectResult.account);
            setGroups(extractGroups(redirectResult.account));
          } else if (accounts.length > 0) {
            // If we have accounts but no redirect result, set the first account as active
            instance.setActiveAccount(accounts[0]);
            setGroups(extractGroups(accounts[0]));
          }
        }

        // Check if we have an active account
        const activeAccount = instance.getActiveAccount();
        
        if (activeAccount && isMounted) {
          // Get access token and set it in API service
          try {
            const accessToken = await acquireApiToken(activeAccount);

            if (accessToken && isMounted) {
              apiService.setAuthToken(accessToken);
            }
          } catch (tokenError) {
            // Silent token acquisition failed, user might need to login again
            if (process.env.NODE_ENV === 'development') {
              console.error('[AuthContext] Token acquisition failed');
            }
            // Try to login interactively
            if (isMounted) {
              try {
                const result = await instance.loginPopup(loginRequest);
                if (result?.accessToken && isMounted) {
                  apiService.setAuthToken(result.accessToken);
                  instance.setActiveAccount(result.account);
                  setGroups(extractGroups(result.account));
                }
              } catch (loginError) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('[AuthContext] Interactive login failed');
                }
              }
            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[AuthContext] Authentication initialization error');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
    
    return () => {
      isMounted = false;
    };
  }, [acquireApiToken, instance, accounts]);

  const login = useCallback(async () => {
    try {
      setIsLoading(true);

      // Use popup login with configuration from authConfig
      const result = await instance.loginPopup(loginRequest);

      if (result?.accessToken) {
        apiService.setAuthToken(result.accessToken);
        // Set the active account after successful login
        instance.setActiveAccount(result.account);
        setGroups(extractGroups(result.account));
      }

      // The authentication state should update automatically via MSAL hooks
      // Give it a moment to update before clearing loading
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[AuthContext] Login error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      apiService.removeAuthToken();
      setGroups([]);
      await instance.logoutPopup();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[AuthContext] Logout error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated || !account) {
      return null;
    }

    try {
      const start = performance.now();
      const accessToken = await acquireApiToken(account);
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuthContext] acquireApiToken succeeded in', Math.round(performance.now() - start), 'ms');
      }

      return accessToken;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[AuthContext] Token acquisition error', error);
      }
      return null;
    }
  }, [acquireApiToken, isAuthenticated, account]);

  useEffect(() => {
    setGroups(extractGroups(account));
  }, [account]);

  const updateGroups = useCallback((mutator: (previous: string[]) => string[]) => {
    setGroups(prev => mutator(prev));
  }, []);

  const hasAnyGroup = (requiredGroups?: string[]): boolean => {
    if (!requiredGroups || requiredGroups.length === 0) {
      return true;
    }

    if (process.env.NODE_ENV === 'development' && requiredGroups.some(group => typeof group !== 'string')) {
      console.warn('[AuthContext] requiredGroups contains non-string values');
    }

    const normalizedRequired = requiredGroups.filter((group): group is string => typeof group === 'string' && group.trim().length > 0);

    if (normalizedRequired.length === 0) {
      return true;
    }

    return normalizedRequired.some(required => groups.includes(required));
  };

  const value: AuthContextType = {
    isAuthenticated,
    user: account,
    login,
    logout,
    getAccessToken,
    isLoading,
    groups,
    hasAnyGroup,
    updateGroups
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
